import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, CellStyle } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { getPromptVersions, type Prompt } from '../../../services/promptsApi';
import { useErrorDialog } from '../../../hooks/useErrorDialog';
import { ErrorDialog } from '../../../components/common/errorDialog';
import { CustomButton } from '../../../components/common/customButton';
import { CustomTextInput } from '../../../components/common/inputFields/customTextInput';
import { formatDateTime } from '../../../utils/formatDate';
import '../../../assets/css/AGGrid-custom.css';
import '../../../assets/css/Text-General.css';

ModuleRegistry.registerModules([AllCommunityModule]);

interface VersionHistoryProps {
  promptKey: string | null;
}

export const VersionHistory = ({ promptKey }: VersionHistoryProps) => {
  const gridRef = useRef<AgGridReact>(null);
  const [versions, setVersions] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<Prompt | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  useEffect(() => {
    if (promptKey) {
      fetchVersions();
    } else {
      setVersions([]);
    }
  }, [promptKey]);

  const fetchVersions = async () => {
    if (!promptKey) return;

    setLoading(true);
    const response = await getPromptVersions(promptKey);

    if (response.success && response.data) {
      // Sort by version descending (newest first)
      const sorted = [...response.data].sort((a, b) => b.version - a.version);
      setVersions(sorted);
    } else {
      showErrorFromResponse(response);
    }
    setLoading(false);
  };


  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedVersion(null);
  };

  const columnDefs: ColDef[] = useMemo(() => [
    {
      field: 'version',
      headerName: 'Version',
      width: 120,
      valueFormatter: (params) => `v${params.value}`,
      cellStyle: (params) => {
        const isActive = params.data?.is_active;
        const color = isActive ? '#1B7A63' : '#8E8E93';
        return { color, fontWeight: '500' } as CellStyle;
      },
      headerClass: 'ag-left-aligned-header',
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      valueFormatter: (params) => params.value ? 'Active' : 'Inactive',
      cellStyle: (params) => {
        const isActive = params.value;
        const color = isActive ? '#1B7A63' : '#8E8E93';
        return { color, fontWeight: '500' } as CellStyle;
      },
      headerClass: 'ag-left-aligned-header',
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 180,
      valueFormatter: (params) => formatDateTime(params.value),
      headerClass: 'ag-left-aligned-header',
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      width: 180,
      valueFormatter: (params) => formatDateTime(params.value),
      headerClass: 'ag-left-aligned-header',
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      width: 200,
      valueGetter: (params) => params.data?.created_by || '-',
      headerClass: 'ag-left-aligned-header',
    },
  ], []);


  if (!promptKey) {
    return (
      <Alert severity="info">
        Select a prompt from the list or click "View Versions" on a prompt to see its version history.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Typography className="text-general-page-subheader">
          Version History: {promptKey}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          All versions of this prompt (including inactive ones)
        </Typography>
      </Box>

      {versions.length === 0 ? (
        <Alert severity="info">No version history found for this prompt.</Alert>
      ) : (
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <div className="ag-theme-alpine" style={{ width: '100%', height: '100%' }}>
            <AgGridReact
              ref={gridRef}              rowData={versions}
              columnDefs={columnDefs}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              rowHeight={30}
              getRowId={(params) => params.data.id.toString()}
            />
          </div>
        </Box>
      )}

      {/* View Version Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Version {selectedVersion?.version} - {selectedVersion?.prompt_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Prompt Key:</strong> {selectedVersion?.prompt_key}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Description:</strong> {selectedVersion?.description || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Status:</strong>{' '}
              <Chip
                label={selectedVersion?.is_active ? 'Active' : 'Inactive'}
                size="small"
                color={selectedVersion?.is_active ? 'success' : 'default'}
                sx={{ ml: 1 }}
              />
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Created:</strong> {selectedVersion ? formatDateTime(selectedVersion.created_at) : '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Updated:</strong> {selectedVersion ? formatDateTime(selectedVersion.updated_at) : '-'}
            </Typography>
          </Box>
          <CustomTextInput
            label="Prompt Content"
            value={selectedVersion?.prompt_content || ''}
            onChange={() => {}}
            multiline
            disabled
          />
        </DialogContent>
        <DialogActions>
          <CustomButton
            handleButtonPress={handleCloseViewDialog}
            isButtonDisabled={false}
            text="Close"
            solid={false}
          />
        </DialogActions>
      </Dialog>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};
